/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { useAppData } from '../../context/AppDataContext';
import { calendarAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { symbolFor } from '../../utils/currencyTotals';

const TYPE_COLORS = {
  'rent': '#3b82f6',
  'rent-due': '#3b82f6',
  'licence-expiry': '#f59e0b',
  'licence': '#f59e0b',
  'project': '#8b5cf6',
  'vacant': '#6b7280',
  'mortgage': '#dc2626',
  'custom': '#10b981',
};

const TYPE_LABELS = {
  'rent': '💰 Rent Due',
  'rent-due': '💰 Rent Due',
  'licence-expiry': '📋 Licence',
  'licence': '📋 Licence',
  'project': '🔨 Project',
  'vacant': '🏠 Vacant',
  'mortgage': '🏦 Mortgage',
  'custom': '📌 Custom',
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Parse ISO date to local date string yyyy-MM-dd WITHOUT timezone shift
const toLocalDateStr = (isoStr) => {
  if (!isoStr) return '';
  return isoStr.substring(0, 10);
};

// Get yyyy-MM-dd for a given year/month/day (local)
const toDateStr = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
};

export default function Calendar() {
  const { calendarTasks, setCalendarTasks, tenants, payments, properties } = useAppData();
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'list'
  const [selectedDay, setSelectedDay] = useState(null); // date string yyyy-MM-dd
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', type: 'custom', color: '#10b981', description: '', completed: false });
  const [filterType, setFilterType] = useState('all');

  // Normalize tasks - ensure date is local yyyy-MM-dd string
  const tasks = useMemo(() =>
    calendarTasks.map(t => ({
      ...t,
      dateStr: toLocalDateStr(t.date)
    })), [calendarTasks]);

  // Generate virtual rent due events from tenant data
  const rentEvents = useMemo(() => {
    if (!tenants || !payments) return [];
    const events = [];
    const now = new Date();

    // Generate events from 2 months back to 6 months forward
    // so past months are also visible when navigating back
    const windowStart = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const windowEnd   = new Date(now.getFullYear(), now.getMonth() + 7, 1);

    for (const tenant of tenants) {
      if (tenant.status !== 'active') continue;
      if (!tenant.payment_due_day) continue;
      if (!tenant.email && !tenant.first_name) continue;

      const dueDay     = parseInt(tenant.payment_due_day);
      const leaseEnd   = tenant.lease_end ? new Date(tenant.lease_end) : null;
      const leaseStart = tenant.lease_start ? new Date(tenant.lease_start) : null;
      const tenantName = `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim();
      const propAddress = tenant.property_address || '';
      const currency   = tenant.currency || 'GBP';
      const sym        = symbolFor(currency);
      const rentAmount = tenant.rent_amount ? `${sym}${parseFloat(tenant.rent_amount).toFixed(2)}` : '';

      // Get room name from properties
      const prop = properties?.find(p => String(p.id) === String(tenant.property_id));
      const roomName = prop?.rooms?.find(r => String(r.id) === String(tenant.room_id))?.roomName || '';

      // Iterate through each month in the window
      let cursor = new Date(windowStart);
      while (cursor < windowEnd) {
        const yr  = cursor.getFullYear();
        const mo  = cursor.getMonth();
        const monthStr = `${yr}-${String(mo + 1).padStart(2, '0')}`;

        // Skip months before lease start
        if (leaseStart) {
          const leaseStartMonth = new Date(leaseStart.getFullYear(), leaseStart.getMonth(), 1);
          if (cursor < leaseStartMonth) { cursor = new Date(yr, mo + 1, 1); continue; }
        }

        // Stop at lease end for fixed-term tenancies
        if (leaseEnd && cursor > leaseEnd) { cursor = new Date(yr, mo + 1, 1); continue; }

        // Cap due day to days in month (e.g. day 31 in February → 28)
        const daysInMonth = new Date(yr, mo + 1, 0).getDate();
        const actualDay   = Math.min(dueDay, daysInMonth);
        const dateStr     = `${yr}-${String(mo + 1).padStart(2, '0')}-${String(actualDay).padStart(2, '0')}`;

        // Check if payment exists for this tenant/room/month
        const paid = payments.some(p =>
          String(p.property_id) === String(tenant.property_id) &&
          (tenant.property_type === 'hmo'
            ? String(p.room_id) === String(tenant.room_id)
            : String(p.property_id) === String(tenant.property_id)
          ) &&
          p.month === monthStr
        );

        const isPast = dateStr < toDateStr(today.getFullYear(), today.getMonth(), today.getDate());
        const color  = paid ? '#16a34a' : isPast ? '#dc2626' : '#3b82f6';
        const status = paid ? '✅ Paid' : isPast ? '⚠️ Unpaid' : '💰 Due';

        events.push({
          id:          `rent-virtual-${tenant.id}-${monthStr}`,
          type:        'rent-due',
          virtual:     true,
          dateStr,
          title:       `${status} — ${tenantName}${roomName ? ` (${roomName})` : ''} — ${propAddress}${rentAmount ? ` ${rentAmount}` : ''}`,
          description: propAddress,
          color,
          completed:   paid,
          tenantId:    tenant.id,
          month:       monthStr,
        });

        cursor = new Date(yr, mo + 1, 1);
      }
    }

    return events;
  }, [tenants, payments, properties, viewMonth, viewYear]);

  // Build calendar grid for current month
  const calendarGrid = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells = [];

    // Previous month padding
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({ day: daysInPrevMonth - i, isCurrentMonth: false, dateStr: toDateStr(viewYear, viewMonth - 1, daysInPrevMonth - i) });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, isCurrentMonth: true, dateStr: toDateStr(viewYear, viewMonth, d) });
    }

    // Next month padding to complete 6-week grid
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, isCurrentMonth: false, dateStr: toDateStr(viewYear, viewMonth + 1, d) });
    }

    return cells;
  }, [viewYear, viewMonth]);

  // Merge persistent tasks + virtual rent events
  const allTasks = useMemo(() => [...tasks, ...rentEvents], [tasks, rentEvents]);

  // Get tasks for a specific date string
  const getTasksForDate = (dateStr) => {
    return allTasks.filter(t => t.dateStr === dateStr);
  };

  // Stats — only from persistent tasks to avoid virtual event noise
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());
  const upcoming = tasks.filter(t => !t.completed && t.dateStr >= todayStr).length;
  const overdue  = tasks.filter(t => !t.completed && t.dateStr < todayStr).length;
  const completed = tasks.filter(t => t.completed).length;
  const rentUnpaid = rentEvents.filter(e => !e.completed && e.dateStr < todayStr).length;

  // List view - sorted by date, merged
  const listTasks = useMemo(() => {
    let filtered = [...allTasks];
    if (filterType !== 'all') filtered = filtered.filter(t => t.type === filterType);
    return filtered.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
  }, [allTasks, filterType]);

  const allTypes = [...new Set(allTasks.map(t => t.type).filter(Boolean))];

  const goToPrev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const goToNext = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const goToToday = () => { setViewMonth(today.getMonth()); setViewYear(today.getFullYear()); };

  const openAdd = (dateStr = '') => {
    setForm({ title: '', date: dateStr, type: 'custom', color: '#10b981', description: '', completed: false });
    setEditTask(null);
    setShowAddModal(true);
  };
  const openEdit = (task) => {
    setForm({ title: task.title || '', date: task.dateStr || '', type: task.type || 'custom', color: task.color || '#10b981', description: task.description || '', completed: task.completed || false });
    setEditTask(task);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTask) {
        const r = await calendarAPI.update(editTask.id, { ...form });
        setCalendarTasks(prev => prev.map(t => t.id === editTask.id ? r : t));
      } else {
        const newId = `custom-${Date.now()}`;
        const r = await calendarAPI.create({ ...form, id: newId });
        setCalendarTasks(prev => [r, ...prev]);
      }
      setShowAddModal(false);
    } catch (err) { alert(err.error || 'Failed to save task'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this calendar entry?')) return;
    try {
      await calendarAPI.delete(taskId);
      setCalendarTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (err) { alert(err.error || 'Failed to delete'); }
  };

  const handleToggleComplete = async (task) => {
    if (task.virtual) return; // Virtual rent events are read-only — managed via payments
    try {
      const r = await calendarAPI.update(task.id, { ...task, date: task.dateStr, completed: !task.completed });
      setCalendarTasks(prev => prev.map(t => t.id === task.id ? r : t));
    } catch (err) { alert(err.error || 'Failed'); }
  };

  // Day detail modal
  const selectedDayTasks = selectedDay ? getTasksForDate(selectedDay) : [];

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Calendar & Tasks</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('month')}>
            🗓️ Month View
          </button>
          <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('list')}>
            📋 List View
          </button>
          <button className="btn btn-primary" onClick={() => openAdd(todayStr)}>+ Add Task</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card"><div className="stat-label">Upcoming</div><div className="stat-value positive">{upcoming}</div></div>
        <div className="stat-card"><div className="stat-label">Overdue Tasks</div><div className="stat-value negative">{overdue}</div></div>
        <div className="stat-card"><div className="stat-label">Rent Unpaid</div><div className="stat-value negative">{rentUnpaid}</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value">{completed}</div></div>
      </div>

      {viewMode === 'month' ? (
        <div className="card calendar-card">
          {/* Month navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <button className="btn btn-secondary" onClick={goToPrev}>← Prev</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h3 style={{ fontWeight: 700, fontSize: '1.25rem' }}>{MONTHS[viewMonth]} {viewYear}</h3>
              <button className="btn btn-secondary" style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }} onClick={goToToday}>Today</button>
            </div>
            <button className="btn btn-secondary" onClick={goToNext}>Next →</button>
          </div>

          <div className="calendar-grid-wrapper">
          {/* Weekday header row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: '2px',
            marginBottom: '2px',
          }}>
            {WEEKDAYS.map(d => (
              <div key={d} style={{
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '0.75rem',
                padding: '0.4rem 0',
                color: 'var(--text-secondary)',
                background: 'var(--bg-light)',
                borderRadius: '4px',
              }}>{d}</div>
            ))}
          </div>

          {/* Calendar day cells — fixed 90px rows so every cell is identical */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gridAutoRows: '90px',
            gap: '2px',
            width: '100%',
          }}>
            {calendarGrid.map((cell, idx) => {
              const cellTasks = getTasksForDate(cell.dateStr);
              const isToday = cell.dateStr === todayStr;
              const isSelected = cell.dateStr === selectedDay;
              const MAX_SHOW = 2;
              const overflow = cellTasks.length - MAX_SHOW;

              return (
                <div
                  key={idx}
                  onClick={() => { setSelectedDay(cell.dateStr === selectedDay ? null : cell.dateStr); }}
                  style={{
                    height: '90px',
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '4px',
                    overflow: 'hidden',
                    border: `1px solid ${isSelected ? 'var(--brand-green)' : isToday ? '#3b82f6' : 'var(--border-light)'}`,
                    borderRadius: '4px',
                    background: isSelected ? '#f0fdf4' : isToday ? '#eff6ff' : cell.isCurrentMonth ? 'white' : '#f9fafb',
                    cursor: 'pointer',
                    opacity: cell.isCurrentMonth ? 1 : 0.5,
                  }}
                >
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? '#2563eb' : cell.isCurrentMonth ? 'inherit' : 'var(--text-secondary)',
                    marginBottom: '2px'
                  }}>
                    {cell.day}
                  </div>
                  {cellTasks.slice(0, MAX_SHOW).map(task => (
                    <div
                      key={task.id}
                      title={task.title}
                      style={{
                        background: task.color || TYPE_COLORS[task.type] || '#6b7280',
                        color: 'white',
                        fontSize: '0.65rem',
                        padding: '1px 4px',
                        borderRadius: '3px',
                        marginBottom: '1px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        display: 'block',
                        textDecoration: task.completed ? 'line-through' : 'none',
                        opacity: task.completed ? 0.6 : 1,
                      }}
                    >
                      {task.title}
                    </div>
                  ))}
                  {overflow > 0 && (
                    <div
                      title={cellTasks.slice(MAX_SHOW).map(t => t.title).join('\n')}
                      style={{ fontSize: '0.6rem', color: 'var(--brand-green)', fontWeight: 700, marginTop: '1px', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      +{overflow} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>{/* end calendar-grid-wrapper */}

          {/* Selected day detail */}
          {selectedDay && (
            <div onClick={e => e.stopPropagation()} style={{ marginTop: '1.5rem', borderTop: '2px solid var(--border-light)', paddingTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ fontWeight: 700 }}>
                  {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </h4>
                <button className="btn btn-primary" style={{ fontSize: '0.8rem' }} onClick={e => { e.stopPropagation(); openAdd(selectedDay); }}>+ Add</button>
              </div>
              {selectedDayTasks.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No events on this day.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedDayTasks.map(task => (
                    <div key={task.id} onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', border: '1px solid var(--border-light)', borderLeft: `4px solid ${task.color || TYPE_COLORS[task.type] || '#6b7280'}`, borderRadius: '0.375rem', background: task.completed ? 'var(--bg-light)' : 'white' }}>
                      <input type="checkbox" checked={task.completed} onChange={e => { e.stopPropagation(); handleToggleComplete(task); }} style={{ cursor: 'pointer', width: '16px', height: '16px', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, textDecoration: task.completed ? 'line-through' : 'none', color: task.completed ? 'var(--text-secondary)' : 'inherit' }}>{task.title}</div>
                        {task.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{task.description}</div>}
                        <span style={{ fontSize: '0.7rem', background: '#f3f4f6', padding: '0.1rem 0.4rem', borderRadius: '0.25rem', color: 'var(--text-secondary)' }}>
                          {TYPE_LABELS[task.type] || task.type}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                        <button
                          className={`badge ${task.completed ? 'badge-success' : 'badge-warning'}`}
                          style={{ cursor: 'pointer', border: 'none', fontSize: '0.7rem' }}
                          onClick={e => { e.stopPropagation(); handleToggleComplete(task); }}
                        >
                          {task.completed ? '✅ Done' : '⏳ Mark done'}
                        </button>
                        {task.type === 'custom' && <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }} onClick={e => { e.stopPropagation(); openEdit(task); }}>✏️</button>}
                        {task.type === 'custom' && <button className="btn btn-danger" style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }} onClick={e => { e.stopPropagation(); handleDelete(task.id); }}>×</button>}
                        {task.virtual && <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>Auto</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* LIST VIEW */
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button className={`btn ${filterType === 'all' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '0.8rem' }} onClick={() => setFilterType('all')}>All</button>
            {allTypes.map(t => (
              <button key={t} className={`btn ${filterType === t ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '0.8rem' }} onClick={() => setFilterType(t)}>
                {TYPE_LABELS[t] || t}
              </button>
            ))}
          </div>

          {listTasks.length === 0 ? (
            <div className="card"><div className="empty-state"><span>📅</span><h3>No Tasks</h3><p>Add a task or sync reminders from Properties</p></div></div>
          ) : (
            <div className="card">
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr><th>Date</th><th>Title</th><th>Type</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {listTasks.map(task => (
                      <tr key={task.id} style={{ opacity: task.completed ? 0.6 : 1 }}>
                        <td style={{ whiteSpace: 'nowrap', color: task.dateStr < todayStr && !task.completed ? 'var(--danger)' : 'inherit' }}>
                          {formatDate(task.dateStr)}
                          {task.dateStr < todayStr && !task.completed && <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>⚠️</span>}
                        </td>
                        <td style={{ textDecoration: task.completed ? 'line-through' : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: task.color || TYPE_COLORS[task.type] || '#6b7280', flexShrink: 0, display: 'inline-block' }} />
                            {task.title}
                          </div>
                          {task.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{task.description}</div>}
                        </td>
                        <td><span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>{TYPE_LABELS[task.type] || task.type}</span></td>
                        <td>
                          <button
                            className={`badge ${task.completed ? 'badge-success' : task.dateStr < todayStr ? 'badge-danger' : 'badge-warning'}`}
                            style={{ cursor: 'pointer', border: 'none' }}
                            onClick={() => handleToggleComplete(task)}
                          >
                            {task.completed ? '✅ Done' : task.dateStr < todayStr ? '⚠️ Overdue' : '⏳ Pending'}
                          </button>
                        </td>
                        <td>
                          {task.type === 'custom' && (
                            <div style={{ display: 'flex', gap: '0.35rem' }}>
                              <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }} onClick={() => openEdit(task)}>✏️</button>
                              <button className="btn btn-danger" style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }} onClick={() => handleDelete(task.id)}>×</button>
                            </div>
                          )}
                          {task.virtual && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Auto-generated</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Task Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <span className="modal-title">{editTask ? '✏️ Edit Task' : '+ Add Calendar Task'}</span>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required placeholder="e.g. PAT Test Expiry" />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Colour</label>
                  <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} style={{ height: '38px', cursor: 'pointer' }} />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows="2" placeholder="Optional notes..." />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editTask ? '💾 Save' : '✅ Add Task'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
